import { DateTime } from 'luxon';
import { FlaggableIngredient } from './FlaggableIngredients';
import { SeasonalFavoriteKey } from './SeasonalFavorites';

export const DEFAULT_PLATFORM_PERCENT_AS_DECIMAL = 0.25;
export const DEFAULT_PLATFORM_PERCENT = DEFAULT_PLATFORM_PERCENT_AS_DECIMAL * 100;
export const NEW_DEFAULT_PLATFORM_PERCENT_AS_DECIMAL = 0.3;
export const NEW_DEFAULT_PLATFORM_PERCENT = NEW_DEFAULT_PLATFORM_PERCENT_AS_DECIMAL * 100;
export const MIN_PLATFORM_PERCENT = 0;
export const MAX_PLATFORM_PERCENT = 100;
export const MINOR_TO_MAJOR = 100;

export const getPlatformPercent = (
  isNewDefaultPlatformPercent: boolean
): { platformPercentAsDecimal: number; platformPercent: number } => {
  const platformPercentAsDecimal = isNewDefaultPlatformPercent
    ? NEW_DEFAULT_PLATFORM_PERCENT_AS_DECIMAL
    : DEFAULT_PLATFORM_PERCENT_AS_DECIMAL;
  const platformPercent = isNewDefaultPlatformPercent ? NEW_DEFAULT_PLATFORM_PERCENT : DEFAULT_PLATFORM_PERCENT;

  return {
    platformPercentAsDecimal,
    platformPercent,
  };
};

export enum TimeZone {
  LosAngeles = 'America/Los_Angeles',
  NewYork = 'America/New_York',
  Chicago = 'America/Chicago',
  UTC = 'UTC',
}

// Keep in sync with shef-api/src/order-totals/bll/calculateOrderTotals.ts L27
export const SERVICE_FEE_RATE = 0.0925;

/**
 * Cleaning fee per group order for reusable packaging, keyed by meal plan type
 * (`${numUnits}x${numServings}`). Values are in cents. Combinations not listed
 * fall through to the default formula in calculateReusablePackagingCleaningFee.
 *
 * Keys mirror REUSABLE_PACKAGING_DEPOSIT_BY_MEAL_PLAN_TYPE_SPEC — the 55 canonical
 * plan combos exposed by the pricing curve experiment (see
 * scripts/backfill-pricing-curve-v1.ts buildCombos).
 */
export const REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC: Record<string, number> = {
  // numServings = 1
  '4x1': 400,
  '5x1': 800,
  '6x1': 500,
  '8x1': 600,
  '9x1': 1200,
  '10x1': 700,
  '12x1': 800,
  '14x1': 1100,
  '16x1': 1200,
  // numServings = 2
  '2x2': 400,
  '3x2': 600,
  '4x2': 600,
  '5x2': 700,
  '6x2': 800,
  '7x2': 900,
  '8x2': 1100,
  '9x2': 1200,
  '10x2': 1200,
  // numServings = 3
  '2x3': 600,
  '3x3': 800,
  '4x3': 800,
  '5x3': 1000,
  '6x3': 1100,
  '7x3': 1200,
  '8x3': 1200,
  '9x3': 1200,
  '10x3': 1200,
  // numServings = 4
  '1x4': 500,
  '2x4': 600,
  '3x4': 800,
  '4x4': 1200,
  '5x4': 1200,
  '6x4': 1200,
  '7x4': 1200,
  '8x4': 1200,
  '9x4': 1200,
  '10x4': 1200,
  // numServings = 5
  '1x5': 600,
  '2x5': 900,
  '3x5': 1200,
  '4x5': 1200,
  '5x5': 1200,
  '6x5': 1200,
  '7x5': 1200,
  '8x5': 1200,
  '9x5': 1200,
  '10x5': 1200,
  // numServings = 6
  '1x6': 600,
  '2x6': 900,
  '3x6': 1200,
  '4x6': 1200,
  '5x6': 1200,
  '6x6': 1200,
  '7x6': 1200,
  '8x6': 1200,
  '9x6': 1200,
  '10x6': 1200,
};

/**
 * The maximum creation date for a meal plan that can still be grandfathered in
 * to be exempt from a reusable packaging cleaning fee.
 *
 * TODO: Before launching reusable packaging (enabling feature flag),
 * set this to the date / time of right before the feature is launched.
 * */
export const REUSABLE_CLEANING_MEAL_PLAN_CREATION_LAST_GRANDFATHER_DATE = DateTime.fromISO('2025-12-09T01:00:00Z', {
  zone: TimeZone.UTC,
});

export const REUSABLE_CLEANING_MEAL_PLAN_PRICING_V2_START_DATE = new Date('2026-06-29T07:00:00Z');

/**
 * Cleaning fee per group order for reusable packaging under v2 pricing, keyed by
 * `${numEntrees}x${numServingsPerEntree}`. Values are in cents. All 100 combinations
 * for 1–10 entrees × 1–10 servings are present; a missing key falls back to the
 * default formula in calculateReusablePackagingCleaningFee.
 */
export const REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC_V2: Record<string, number> = {
  // numServings = 1
  '1x1': 400,
  '2x1': 400,
  '3x1': 500,
  '4x1': 600,
  '5x1': 800,
  '6x1': 900,
  '7x1': 1000,
  '8x1': 1100,
  '9x1': 1200,
  '10x1': 1300,
  // numServings = 2
  '1x2': 400,
  '2x2': 500,
  '3x2': 700,
  '4x2': 900,
  '5x2': 1000,
  '6x2': 1200,
  '7x2': 1300,
  '8x2': 1400,
  '9x2': 1600,
  '10x2': 1700,
  // numServings = 3
  '1x3': 400,
  '2x3': 700,
  '3x3': 900,
  '4x3': 1100,
  '5x3': 1200,
  '6x3': 1400,
  '7x3': 1600,
  '8x3': 1700,
  '9x3': 1900,
  '10x3': 2100,
  // numServings = 4
  '1x4': 500,
  '2x4': 800,
  '3x4': 1000,
  '4x4': 1200,
  '5x4': 1400,
  '6x4': 1600,
  '7x4': 1800,
  '8x4': 2000,
  '9x4': 2200,
  '10x4': 2400,
  // numServings = 5
  '1x5': 600,
  '2x5': 900,
  '3x5': 1100,
  '4x5': 1400,
  '5x5': 1600,
  '6x5': 1900,
  '7x5': 2100,
  '8x5': 2300,
  '9x5': 2500,
  '10x5': 2700,
  // numServings = 6
  '1x6': 600,
  '2x6': 1000,
  '3x6': 1300,
  '4x6': 1600,
  '5x6': 1800,
  '6x6': 2100,
  '7x6': 2300,
  '8x6': 2600,
  '9x6': 2800,
  '10x6': 3100,
  // numServings = 7
  '1x7': 700,
  '2x7': 1100,
  '3x7': 1400,
  '4x7': 1700,
  '5x7': 2000,
  '6x7': 2300,
  '7x7': 2600,
  '8x7': 2800,
  '9x7': 3100,
  '10x7': 3400,
  // numServings = 8
  '1x8': 800,
  '2x8': 1200,
  '3x8': 1400,
  '4x8': 1900,
  '5x8': 2000,
  '6x8': 2300,
  '7x8': 2800,
  '8x8': 2800,
  '9x8': 3100,
  '10x8': 3400,
  // numServings = 9
  '1x9': 800,
  '2x9': 1200,
  '3x9': 1400,
  '4x9': 1900,
  '5x9': 2000,
  '6x9': 2300,
  '7x9': 2800,
  '8x9': 2800,
  '9x9': 3100,
  '10x9': 3400,
  // numServings = 10
  '1x10': 900,
  '2x10': 1200,
  '3x10': 1400,
  '4x10': 1900,
  '5x10': 2000,
  '6x10': 2300,
  '7x10': 2800,
  '8x10': 2800,
  '9x10': 3100,
  '10x10': 3400,
};

/**
 * One-time refundable deposit for reusable packaging, keyed by meal plan type
 * (`${numUnits}x${numServings}`). Values are in cents. Combinations not listed
 * are unsupported and throw — callers should only invoke this for plan types
 * that exist in the supported set.
 *
 * Keys mirror REUSABLE_PACKAGING_CLEANING_FEE_BY_MEAL_PLAN_TYPE_SPEC — the 55
 * canonical plan combos exposed by the pricing curve experiment (see
 * scripts/backfill-pricing-curve-v1.ts buildCombos).
 */
export const REUSABLE_PACKAGING_DEPOSIT_BY_MEAL_PLAN_TYPE_SPEC: Record<string, number> = {
  // numServings = 1
  '4x1': 1700,
  '5x1': 2300,
  '6x1': 2300,
  '8x1': 2600,
  '9x1': 2700,
  '10x1': 2800,
  '12x1': 3100,
  '14x1': 3500,
  '16x1': 3700,
  // numServings = 2
  '2x2': 1500,
  '3x2': 1700,
  '4x2': 2300,
  '5x2': 2800,
  '6x2': 3500,
  '7x2': 3500,
  '8x2': 3700,
  '9x2': 3900,
  '10x2': 3900,
  // numServings = 3
  '2x3': 1500,
  '3x3': 2300,
  '4x3': 2800,
  '5x3': 3500,
  '6x3': 3500,
  '7x3': 3900,
  '8x3': 4500,
  '9x3': 4500,
  '10x3': 4900,
  // numServings = 4
  '1x4': 1700,
  '2x4': 2600,
  '3x4': 3100,
  '4x4': 3500,
  '5x4': 3900,
  '6x4': 4500,
  '7x4': 4500,
  '8x4': 4900,
  '9x4': 4900,
  '10x4': 4900,
  // numServings = 5
  '1x5': 2300,
  '2x5': 3100,
  '3x5': 3300,
  '4x5': 3900,
  '5x5': 4900,
  '6x5': 4900,
  '7x5': 4900,
  '8x5': 4900,
  '9x5': 4900,
  '10x5': 4900,
  // numServings = 6
  '1x6': 2300,
  '2x6': 3500,
  '3x6': 3700,
  '4x6': 4500,
  '5x6': 4900,
  '6x6': 4900,
  '7x6': 4900,
  '8x6': 4900,
  '9x6': 4900,
  '10x6': 4900,
};

export function getDepositCostByUnitsAndServings(numUnits: number, numServings: number): number {
  const key = `${numUnits}x${numServings}`;
  const deposit = REUSABLE_PACKAGING_DEPOSIT_BY_MEAL_PLAN_TYPE_SPEC[key];
  if (deposit === undefined) {
    throw new Error(`No reusable packaging deposit defined for ${numUnits} mains × ${numServings} servings.`);
  }
  return deposit;
}

export enum DeliveryType {
  DoorDash = 'DOORDASH',
  Internal = 'INTERNAL',
  Unknown = 'UNKNOWN',
  Lalamove = 'LALAMOVE',
  Pickup = 'PICKUP',
  Fulflld = 'FULFLLD',
  ShippedByMail = 'SHIPPED_BY_MAIL', // Deprecated 12-9-2022. Leaving to allow orders to still be seen in receipts
  Nash = 'NASH',
  DirectViaShefs = 'DIRECT_VIA_SHEFS',
  DirectViaNash = 'DIRECT_VIA_NASH', // This is because Nash delivery does not operate the same in pick-up only regions.
  Vamo = 'VAMO',
  EmeraldCity = 'EMERALD_CITY',
  Hungry = 'HUNGRY',
  Eoe = 'EOE',
  Farias = 'FARIAS',
  Frayt = 'FRAYT',
  Logiscal = 'LOGISCAL',
  // Zippy = 'ZIPPY', - Deprecated on 8/3/22
  // Gofor = 'GOFOR', - Deprecated on 8/3/22
  // PARTNER_NAMED_DROPOFF = 'PARTNER_DROPOFF', // We have a delivery partner named DROPOFF. SO confusing. Deprecated in January/2022. Used for NYC
  // FIRST = 'FIRST', -- Deprecated 6/22 - in Seattle
}

// Find more information on these values here:
// https://www.notion.so/Shef-fulfilled-Delivery-in-Pick-Up-Regions-bbbb60fb930f4cebb4c1c80182edb9cc
export enum FulfillmentOption {
  Pickup = 'PICKUP',
  DirectViaShefs = 'DIRECT_VIA_SHEFS',
  DirectViaNash = 'DIRECT_VIA_NASH',
}

export enum NotificationType {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  PUSH = 'PUSH',
}

export enum NotificationStatus {
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
}

export enum DietaryTag {
  ContainsNuts = 'containsNuts',
  DairyFree = 'dairyFree',
  GlutenFree = 'glutenFree',
  Halal = 'halal',
  LowFodmap = 'lowFodmap',
  Organic = 'organic',
  Paleo = 'paleo',
  Vegan = 'vegan',
  Vegetarian = 'vegetarian',
  Whole30 = 'whole30',
  Kosher = 'kosher',
}

/**
 * This needs to be kept in sync with the MealPlanFoodType enum defined in shef-api/src/food-items/FoodItem.graphql
 * in the event that more MealPlanFoodType values are added
 */
export const MAIN = 'main';
export const SIDE = 'side';
export const STAPLE = 'staple';
export const LUNCH = 'lunch';
export const DRINK = 'drink';
export const DESSERT = 'dessert';
export const DESSERT_MULTI = 'dessertMulti';
export const SNACK = 'snack';
export const KIDS_MAIN = 'kidsMain';
export const KIDS_SNACK = 'kidsSnack';
export type MealPlanFoodType =
  | typeof MAIN
  | typeof SIDE
  | typeof STAPLE
  | typeof KIDS_MAIN
  | typeof KIDS_SNACK
  | typeof DRINK
  | typeof LUNCH
  | typeof SNACK
  | typeof DESSERT
  | typeof DESSERT_MULTI
  | undefined
  | null;

export { FlaggableIngredient };

/**
 * Needs to be kept in sync with the DietaryRestriction enum in shef-api/src/taste-profile/TasteProfile.graphql
 */
export type DietaryRestriction =
  | Extract<DietaryTag, DietaryTag.GlutenFree | DietaryTag.DairyFree>
  | FlaggableIngredient;

export enum PaymentCaptureType {
  /**
   * Collect payment while the user is offline, at some time in the future. In
   * order to do this, the user's credit card information is stored by stripe.
   * Capturing payments while the user is offline does not guarantee that a
   * payment can actually be completed.
   */

  // NOTE THIS WAS DEPRECATED WHEN WE MOVED TO GROUP ORDERS around dec 2022
  STRIPE_OFFLINE_CHARGE = 'STRIPE_OFFLINE_CHARGE', // DEPRECATED

  /**
   * Create a payment intent immediately, essentially placing a hold for a
   * specific dollar amount on the consumer's card. The payment intent will
   * expire in 7 days, unless the payment is explicitly  captured, transfering
   * the money to our stripe account.
   */
  STRIPE_IMMEDIATE_PAYMENT_INTENT = 'STRIPE_IMMEDIATE_PAYMENT_INTENT',
}

export const ORDER_STATUS_TO_REVERSE_SHEF_PAYMENT: readonly string[] = [
  'SHEF',
  'USER_CANCELLED',
  'SUBSCRIPTION_CANCELED',
];

export const Time = {
  ONE_YEAR_IN_MS: 365 * 24 * 60 * 60 * 1000,
  ONE_WEEK_IN_MS: 7 * 24 * 60 * 60 * 1000,
  ONE_DAY_IN_MS: 24 * 60 * 60 * 1000,
  ONE_HOUR_IN_MS: 60 * 60 * 1000,
  ONE_MINUTE_IN_MS: 60 * 1000,
  ONE_SECOND_IN_MS: 1000,
};

/**
 * Minimum number of days to wait after a delivery before prompting for reviews.
 */
export const GROUP_ORDER_REVIEW_PROMPT_DELAY_DAYS = 3;

export const noOp = (): void => {
  /* noOp */
};

export const POST_REVIEW_PROMO_TIME_LIMIT_HOURS = 2;

export const MIN_DATE = DateTime.utc(1970);
export const MAX_DATE = DateTime.utc(2999);

export const MAX_STARS = 5;

export enum ColorTheme {
  LIGHT = 'LIGHT',
  DARK = 'DARK',
}

export const FREE_DELIVERY_MINIMUM_THRESHOLD = 6000;
export const DELIVERY_FEE = 599;
export const HUBLESS_DELIVERY_MAX_DISTANCE = 35; // miles

export const DELIVERY_IN_PICKUP_REGION_ORDER_MINIMUM = 2500;

export const SAME_DAY_PROMO_SUFFIX = 'same-day-free-delivery';

export const DUMMY_BARCODE_VALUE = 'Barcode Coming Soon!';

// New referral values (new C2C system)
export const DEFAULT_REFERRAL_VALUE_NEW = 3000; // $30 off each of first 2 orders
export const DEFAULT_REFERRER_VALUE_NEW = 6000; // $60 off one order

export const DEFAULT_REFERRAL_VALUE = 2500;
export const DEFAULT_REFERRER_VALUE = 10000;
export const DEFAULT_REFERRAL_USES_PER_USER = 2;

export const ZIP_CODE_REGEX = /^\d{5}$/;

export const UNAVAILABLE_IMAGE_URL =
  'https://shef-general.s3-us-west-1.amazonaws.com/static/media/img_unavailable.012cee8d.jpg';
export const EMAIL_ANYWHERE_REGEX =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

export const MEAL_PLAN_DEFAULT_SIDE_PRICE = 300;
export const FLEXIBLE_SIDES_DEFAULT_STAPLE_PRICE = 300;
export const FLEXIBLE_SIDES_DEFAULT_SIDE_PRICE = 600;

export const MEAL_PLAN_DEFAULT_KIDS_SNACK_PRICE = 600;

export const MEAL_PLAN_ADD_ON_TITLES = {
  [LUNCH]: 'Lunch',
  [KIDS_MAIN]: 'Kids Meals',
  [KIDS_SNACK]: 'Kids Snacks',
  [DESSERT]: 'Desserts',
  [DRINK]: 'Drinks',
  [SNACK]: 'Snacks',
} as const;
export const ADD_ON_TYPES = [LUNCH, KIDS_MAIN, KIDS_SNACK, DESSERT, DRINK, SNACK] as const;
export const MAXIMUM_MEAL_PLAN_SERVINGS = 6;

export const MEAL_PLAN_SHEFS_TO_DEPRIORITIZE: { [key: string]: boolean } = {
  'sukhdeep-k-2': true,
  'deepti-g-1': true,
  'prachi-p-24': true,
  'joyal-g': true,
};

export const MEAL_PLAN_NY_PLATE_SHEFS: { [key: string]: number } = {};

export const LEGACY_FAKE_MEAL_PLAN_SHEF_IDS: readonly string[] = [
  'meal-plan-w',
  'meal-plan-w-1',
  'meal-plan-w-2',
  'meal-plan-w-3',
];

export const HST_TO_UTC_OFFSET = 10;

/** Minimum days before a menu assignment removal takes effect */
export const DEFAULT_MENU_REMOVAL_DAYS = 7;

/**
 * Earliest date (YYYY-MM-DD) a shef can schedule a Limited Time item to start.
 * Soft-launch floor — once today passes this date, the min reverts to today.
 */
export const LT_EARLIEST_WINDOW_START = '2026-05-03';

/**
 * Markets launching core / Limited Time / Seasonal in the June 2026 wave: Chicago (5),
 * Seattle (6), Boston (7), LA (8), DC (9), Austin (10).
 */
export const JUNE_LAUNCH_REGION_IDS: readonly number[] = [5, 6, 7, 8, 9, 10];

/** First orderable Limited Time delivery date in the June 2026 launch markets. */
export const JUNE_LAUNCH_LT_EARLIEST_WINDOW_START = '2026-06-28';

/**
 * Earliest date a shef can start an LT window. June-launch markets can't start before their first
 * orderable day; established markets fall back to {@link LT_EARLIEST_WINDOW_START}.
 */
export function getLtEarliestWindowStart(regionId?: number | null): string {
  if (regionId != null && JUNE_LAUNCH_REGION_IDS.includes(regionId)) {
    return JUNE_LAUNCH_LT_EARLIEST_WINDOW_START;
  }
  return LT_EARLIEST_WINDOW_START;
}

/** Regions where Limited Time consumer surfaces are hidden: Boston (7). */
export const LT_HIDDEN_REGION_IDS: readonly number[] = [7];

export function isLtHiddenRegion(regionId?: number | null): boolean {
  return regionId != null && LT_HIDDEN_REGION_IDS.includes(regionId);
}

/** Regions where Seasonal consumer surfaces are hidden. Currently none — Seasonal live everywhere. */
export const SEASONAL_HIDDEN_REGION_IDS: readonly number[] = [];

export function isSeasonalHiddenRegion(regionId?: number | null): boolean {
  return regionId != null && SEASONAL_HIDDEN_REGION_IDS.includes(regionId);
}

/**
 * Earliest date (YYYY-MM-DD) the Limited Time pill, filter, and For You section
 * are surfaced to consumers. Set ahead of LT_EARLIEST_WINDOW_START so the pill
 * appears as a teaser before items actually become orderable.
 */
export const LT_CONSUMER_VISIBLE_FROM = '2026-04-30';

/**
 * Maximum span (in weeks) of a Limited Time assignment's window — enforced on the
 * date picker and in the backend mutation. The schedule step also defaults
 * windowEnd to windowStart + this value so the shef sees the full available range.
 */
export const MAX_LT_WINDOW_WEEKS = 4;

export const MEAL_PLAN_30_PROMO_CODES: readonly string[] = [
  'meal_plans_30_bay',
  'meal_plans_30_ny',
  'meal_plans_30_chicago',
  'meal_plans_30_seattle',
  'meal_plans_30_la',
  'meal_plans_30_austin',
  'meal_plans_30_dc',
  'meal_plans_30_boston',
];

export const MEAL_PLAN_50_PROMO_CODES: readonly string[] = [
  'meal_plans_50_bay',
  'meal_plans_50_ny',
  'meal_plans_50_chicago',
  'meal_plans_50_seattle',
  'meal_plans_50_la',
];

export const WSS_NEW_USER_PROMO_CODE_CONTROL = 'personalshef30';
export const WSS_NEW_USER_PROMO_CODE_TREATMENT = 'shefmade40';

export const MEAL_PLAN_NEW_USER_PROMO_CODES: readonly string[] = [
  ...MEAL_PLAN_30_PROMO_CODES,
  WSS_NEW_USER_PROMO_CODE_CONTROL,
  WSS_NEW_USER_PROMO_CODE_TREATMENT,
];

export const MARKETPLACE_AVAILABLE_DELIVERY_DAYS: { [key: number]: number[] } = {
  // Bay Area Not Saturday
  1: [1, 2, 3, 4, 5, 7],
  // NY Monday Tuesday Thursday Sunday
  2: [1, 2, 4, 7],
  // Chicago Monday Tuesday Thursday Sunday
  5: [1, 2, 4, 7],
  // Seattle Not Friday or Saturday
  6: [1, 2, 3, 4, 7],
  // LA Not Saturday
  8: [1, 2, 3, 4, 5, 7],
};

export const FLEXIBLE_SIDES_MP_DELIVERY_DAYS: { [key: number]: number[] } = {
  // Bay: Sunday, Monday, Tuesday, Wednesday
  1: [1, 2, 3, 7],
  // NY, Seattle, Chicago: Monday, Tuesday, Thursday, Sunday
  2: [1, 2, 4, 7], // NY
  5: [1, 2, 4, 7], // Chicago
  6: [1, 2, 4, 7], // Seattle
  // Boston: Monday, Tuesday, Sunday
  7: [1, 2, 7],
  // LA: Monday, Tuesday, Friday, Sunday
  8: [1, 2, 5, 7],
  // DC: Monday, Tuesday, Sunday
  9: [1, 2, 7],
  // Austin: Monday, Tuesday, Sunday
  10: [1, 2, 7],
  // Houston, Dallas: Monday, Sunday. Supplied from Austin by line drive; the receiving hubs
  // run Monday and Sunday only, so Austin's Tuesday is not deliverable here.
  3: [1, 7],
  11: [1, 7],
};

export const REGION_ID_TO_REGION_PROMO_CODE: Record<number, string> = {
  1: 'meal_plans_30_bay',
  2: 'meal_plans_30_ny',
  5: 'meal_plans_30_chicago',
  6: 'meal_plans_30_seattle',
  7: 'meal_plans_30_boston',
  8: 'meal_plans_30_la',
  9: 'meal_plans_30_dc',
  10: 'meal_plans_30_austin',
};

export const INDIAN_REGIONS = ['NORTH_INDIAN', 'SOUTH_INDIAN', 'WEST_INDIAN', 'EAST_INDIAN', 'INDO_CHINESE'];

// Commenting out cuisines that aren't available now. Leaving them in for future availability
export const CUISINE_MAPPING = {
  // BAY
  1: {
    indianmp: [
      'INDIAN',
      'PAKISTANI',
      'BANGLADESHI',
      'THAI',
      'VIETNAMESE',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
      'AFRICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'THAI',
      'VIETNAMESE',
      'PAKISTANI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
      'AFRICAN',
    ],
    NORTH_INDIAN: ['DELHI', 'PUNJABI', 'MUGHLAI'],
    SOUTH_INDIAN: ['ANDHRA', 'KERALA', 'TAMILIAN'],
    WEST_INDIAN: ['MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },
  // NY
  2: {
    indianmp: ['INDIAN', 'AMERICAN', 'JAPANESE', 'KOREAN', 'MIDDLE_EASTERN'],
    nonindian: ['AMERICAN', 'INDIAN', 'JAPANESE', 'KOREAN', 'MIDDLE_EASTERN'],
    NORTH_INDIAN: ['DELHI', 'PUNJABI', 'MUGHLAI'],
    SOUTH_INDIAN: ['ANDHRA', 'KERALA', 'TAMILIAN'],
    WEST_INDIAN: ['MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },

  // CHI
  5: {
    indianmp: [
      'INDIAN',
      'BANGLADESHI',
      'INDONESIAN',
      'THAI',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'JAPANESE',
      'KOREAN',
      'INDONESIAN',
      'THAI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
    ],
    NORTH_INDIAN: ['PUNJABI', 'RAJASTHANI', 'DELHI'],
    SOUTH_INDIAN: ['TAMILIAN', 'ANDHRA', 'HYDERABADI', 'KERALA', 'MYSORE'],
    WEST_INDIAN: ['GUJARATI', 'MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
  },
  // SEA
  6: {
    indianmp: [
      'INDIAN',
      'PAKISTANI',
      'BANGLADESHI',
      'THAI',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'THAI',
      'PAKISTANI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
    ],
    NORTH_INDIAN: ['PUNJABI', 'MUGHLAI', 'RAJASTHANI'],
    SOUTH_INDIAN: ['KARNATAKA', 'TAMILIAN'],
    WEST_INDIAN: ['GUJARATI', 'MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },
  7: {
    indianmp: [
      'INDIAN',
      'PAKISTANI',
      'BANGLADESHI',
      'THAI',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'THAI',
      'PAKISTANI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
    ],
    NORTH_INDIAN: ['PUNJABI', 'MUGHLAI', 'RAJASTHANI'],
    SOUTH_INDIAN: ['KARNATAKA', 'TAMILIAN'],
    WEST_INDIAN: ['GUJARATI', 'MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },
  8: {
    indianmp: [
      'INDIAN',
      'PAKISTANI',
      'BANGLADESHI',
      'THAI',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'THAI',
      'PAKISTANI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
    ],
    NORTH_INDIAN: ['PUNJABI', 'MUGHLAI', 'RAJASTHANI'],
    SOUTH_INDIAN: ['KARNATAKA', 'TAMILIAN'],
    WEST_INDIAN: ['GUJARATI', 'MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },
  9: {
    indianmp: [
      'INDIAN',
      'PAKISTANI',
      'BANGLADESHI',
      'THAI',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'THAI',
      'PAKISTANI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
    ],
    NORTH_INDIAN: ['PUNJABI', 'MUGHLAI', 'RAJASTHANI'],
    SOUTH_INDIAN: ['KARNATAKA', 'TAMILIAN'],
    WEST_INDIAN: ['GUJARATI', 'MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },
  10: {
    indianmp: [
      'INDIAN',
      'PAKISTANI',
      'BANGLADESHI',
      'THAI',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'MIDDLE_EASTERN',
      'MEDITERRANEAN',
      'AMERICAN',
      'LATIN_AMERICAN',
    ],
    nonindian: [
      'AMERICAN',
      'MEDITERRANEAN',
      'LATIN_AMERICAN',
      'INDIAN',
      'CHINESE',
      'JAPANESE',
      'KOREAN',
      'THAI',
      'PAKISTANI',
      'BANGLADESHI',
      'MIDDLE_EASTERN',
    ],
    NORTH_INDIAN: ['PUNJABI', 'MUGHLAI', 'RAJASTHANI'],
    SOUTH_INDIAN: ['KARNATAKA', 'TAMILIAN'],
    WEST_INDIAN: ['GUJARATI', 'MAHARASHTRIAN'],
    EAST_INDIAN: ['BENGALI'],
    INDO_CHINESE: [],
  },
};

export const MEAL_PLAN_REGIONS_IDS = Object.keys(FLEXIBLE_SIDES_MP_DELIVERY_DAYS).map((key) => parseInt(key, 10));
export const REUSABLE_PACKAGING_REGION_IDS = [1, 2];
export const REUSABLE_PACKAGING_EXCLUDED_HUB_IDS: readonly number[] = [18, 81, 36]; // SAC, NJ, RVS
export const INGREDIENT_PRIORITIES_REGION_IDS = [2];
export const ORGANIC_ENABLED_REGION_IDS = [2];

export const isUserInMealPlanRegion = (userRegion: number): boolean => MEAL_PLAN_REGIONS_IDS.includes(userRegion);

export const shouldSkipDoorToDoorPopUpRegion = (userRegion: number): boolean =>
  MEAL_PLAN_REGIONS_IDS.includes(userRegion);

export const ALL_CUISINES = [
  {
    cuisineFilter: 'AFRICAN',
    rootCuisineFilter: 'AFRICAN',
    title: 'African',
    isSelected: false,
  },
  {
    cuisineFilter: 'AMERICAN',
    rootCuisineFilter: 'AMERICAN',
    title: 'American',
    isSelected: false,
  },
  {
    cuisineFilter: 'ANDHRA',
    rootCuisineFilter: 'SOUTH_INDIAN',
    title: 'Andhra',
    isSelected: false,
  },
  {
    cuisineFilter: 'BANGLADESHI',
    rootCuisineFilter: 'SOUTHEAST_ASIAN',
    title: 'Bangladeshi',
    isSelected: false,
  },
  {
    cuisineFilter: 'BENGALI',
    rootCuisineFilter: 'EAST_INDIAN',
    title: 'Bengali',
    isSelected: false,
  },
  {
    cuisineFilter: 'CHINESE',
    rootCuisineFilter: 'CHINESE',
    title: 'Chinese',
    isSelected: false,
  },
  {
    cuisineFilter: 'DELHI',
    rootCuisineFilter: 'NORTH_INDIAN',
    title: 'Delhi',
    isSelected: false,
  },
  {
    cuisineFilter: 'GUJARATI',
    rootCuisineFilter: 'GUJARATI',
    title: 'Gujarati',
    isSelected: false,
  },
  {
    cuisineFilter: 'HYDERABADI',
    rootCuisineFilter: 'HYDERABADI',
    title: 'Hyderabadi',
    isSelected: false,
  },
  {
    cuisineFilter: 'INDIAN',
    rootCuisineFilter: 'INDIAN',
    title: 'Indian',
    isSelected: false,
  },
  {
    cuisineFilter: 'NORTH_INDIAN',
    rootCuisineFilter: 'INDIAN',
    title: 'North Indian',
    isSelected: false,
  },
  {
    cuisineFilter: 'SOUTH_INDIAN',
    rootCuisineFilter: 'INDIAN',
    title: 'South Indian',
    isSelected: false,
  },
  {
    cuisineFilter: 'WEST_INDIAN',
    rootCuisineFilter: 'INDIAN',
    title: 'West Indian',
    isSelected: false,
  },
  {
    cuisineFilter: 'EAST_INDIAN',
    rootCuisineFilter: 'INDIAN',
    title: 'East Indian',
    isSelected: false,
  },
  {
    cuisineFilter: 'INDONESIAN',
    rootCuisineFilter: 'SOUTHEAST_ASIAN',
    title: 'Indonesian',
    isSelected: false,
  },
  {
    cuisineFilter: 'INDO_CHINESE',
    rootCuisineFilter: 'INDIAN',
    title: 'Indo Chinese',
    isSelected: false,
  },
  {
    cuisineFilter: 'JAPANESE',
    rootCuisineFilter: 'JAPANESE',
    title: 'Japanese',
    isSelected: false,
  },
  {
    cuisineFilter: 'KARNATAKA',
    rootCuisineFilter: 'KARNATAKA',
    title: 'Karnataka',
    isSelected: false,
  },
  {
    cuisineFilter: 'KERALA',
    rootCuisineFilter: 'SOUTH_INDIAN',
    title: 'Kerala',
    isSelected: false,
  },
  {
    cuisineFilter: 'KOREAN',
    rootCuisineFilter: 'KOREAN',
    title: 'Korean',
    isSelected: false,
  },
  {
    cuisineFilter: 'LATIN_AMERICAN',
    rootCuisineFilter: 'LATIN_AMERICAN',
    title: 'Latin American',
    isSelected: false,
  },
  {
    cuisineFilter: 'MAHARASHTRIAN',
    rootCuisineFilter: 'WEST_INDIAN',
    title: 'Maharashtrian',
    isSelected: false,
  },
  {
    cuisineFilter: 'MEDITERRANEAN',
    rootCuisineFilter: 'MEDITERRANEAN',
    title: 'Mediterranean',
    isSelected: false,
  },
  {
    cuisineFilter: 'MIDDLE_EASTERN',
    rootCuisineFilter: 'MIDDLE_EASTERN',
    title: 'Middle Eastern',
    isSelected: false,
  },
  {
    cuisineFilter: 'MUGHLAI',
    rootCuisineFilter: 'NORTH_INDIAN',
    title: 'Mughlai',
    isSelected: false,
  },
  {
    cuisineFilter: 'MYSORE',
    rootCuisineFilter: 'MYSORE',
    title: 'Mysore',
    isSelected: false,
  },
  {
    cuisineFilter: 'PAKISTANI',
    rootCuisineFilter: 'PAKISTANI',
    title: 'Pakistani',
    isSelected: false,
  },
  {
    cuisineFilter: 'PUNJABI',
    rootCuisineFilter: 'NORTH_INDIAN',
    title: 'Punjabi',
    isSelected: false,
  },
  {
    cuisineFilter: 'RAJASTHANI',
    rootCuisineFilter: 'RAJASTHANI',
    title: 'Rajasthani',
    isSelected: false,
  },
  {
    cuisineFilter: 'TAMILIAN',
    rootCuisineFilter: 'SOUTH_INDIAN',
    title: 'Tamilian',
    isSelected: false,
  },
  {
    cuisineFilter: 'THAI',
    rootCuisineFilter: 'SOUTHEAST_ASIAN',
    title: 'Thai',
    isSelected: false,
  },
  {
    cuisineFilter: 'VIETNAMESE',
    rootCuisineFilter: 'SOUTHEAST_ASIAN',
    title: 'Vietnamese',
    isSelected: false,
  },
];

export const STAPLE_DIETARY_TAGS_TO_IGNORE = [
  DietaryTag.Paleo,
  DietaryTag.Whole30,
  DietaryTag.DairyFree,
  DietaryTag.Halal,
  DietaryTag.LowFodmap,
  DietaryTag.Kosher,
];

export const SHEF_MAXIMUM_CAPACITY_PERCENTAGE = 0.75;

export const SOUTH_ASIAN_REGIONAL_CUISINE_CATEGORIES = new Set([
  'BIHARI',
  'CHENNAI',
  'DELHI',
  'LAHORI',
  'TIBETAN',
  'BHUTANESE',
  'MALDIVIAN',
  'MALVANI',
  'MARATHI',
  'MUMBAI',
  'JAIN',
  'BENGALI',
  'ODISHA',
  'EAST_INDIAN',
  'NORTH_INDIAN',
  'INDO_CHINESE',
  'SOUTH_INDIAN',
  'WEST_INDIAN',
  'KOLHAPURI',
  'MARWARI',
  'SINDHI',
  'CHHATTISGARHI',
  'LUCKNOWI',
  'KASHMIRI',
  'RAJASTHANI',
  'AWADHI',
  'MUGHLAI',
  'PUNJABI',
  'HIMALAYAN',
  'PAKISTANI',
  'INDIAN',
  'BANGLADESHI',
  'SRI_LANKAN',
  'NEPALESE',
  'KARNATAKA',
  'CHETTINAD',
  'ANDHRA',
  'COIMBATORE',
  'MANGALOREAN',
  'COORG',
  'TAMILIAN',
  'KERALA',
  'UDUPI',
  'HYDERABADI',
  'MYSORE',
  'GUJARATI',
  'MAHARASHTRIAN',
  'KONKANI',
  'GOAN',
]);

export const KID_FRIENDLY_MEAL_PLAN_FOOD_ITEM_IDS: Record<number, Set<number>> = {
  1: new Set([
    28156, 28510, 29270, 41389, 63615, 65797, 76785, 77029, 77932, 81469, 96639, 109226, 127624, 128051, 136669, 138398,
    143027, 145427, 149600, 151107, 153969, 160867, 167595, 170609, 172158, 176367, 179702, 185803, 186759, 188763,
    191991, 193403, 195418, 196771, 237559, 251402, 255393, 274344, 274355, 274547, 275151, 276294, 282497, 283461,
    283489, 283493, 283495, 283498, 283506, 285934, 286293, 286701, 286706, 286734, 286742, 289395, 289505, 289510,
    289747, 290193, 290200, 290233, 290353, 290394, 290704, 290879, 290880, 290881, 291322, 291601, 292094, 292096,
    292553, 293197, 293406, 293446, 293465, 293469, 294233, 294675, 294677, 294865, 294868, 294869, 294871, 294873,
    295113, 295355, 295361, 295365, 295375, 295487, 295580, 295669, 295867, 295871, 295877, 295908, 296084, 296124,
    296125, 296127, 296129, 296133, 296135, 296136, 296768, 296790, 297815, 297819, 297821, 297963, 298369, 298370,
    298519, 299555, 299690, 299691, 300268, 300269, 300405, 300421, 300528, 300530, 301128, 301274, 301318, 301331,
    301616, 301622, 301897, 302125, 302594,
  ]),
  2: new Set([
    8142, 15612, 15887, 24679, 63290, 81467, 109256, 113023, 131087, 136173, 157892, 161020, 169104, 190876, 207858,
    272373, 281777, 283170, 283173, 283174, 283261, 283492, 283704, 286294, 286735, 287129, 289756, 289821, 289824,
    289937, 290057, 290082, 290083, 290185, 290288, 290788, 290909, 290930, 291369, 291582, 291608, 291828, 292012,
    292105, 292494, 292502, 293362, 293411, 293447, 293449, 293463, 293464, 293799, 294070, 294071, 294072, 294216,
    295367, 295372, 295373, 295379, 295436, 295438, 295453, 295583, 295876, 295911, 296250, 297816, 297820, 298821,
    298872, 299180, 299198, 299686, 299689, 299714, 299716, 299721, 299725, 300441, 300443, 300449, 300569, 300574,
    300575, 300672, 301302, 301319, 301321, 301327, 301930, 301938, 302596, 303188,
  ]),
  3: new Set([
    2682, 15293, 15682, 20764, 21647, 29406, 65804, 80349, 83249, 97284, 107008, 115183, 128513, 138373, 141523, 148249,
    153527, 160868, 175379, 181628, 187915, 188767, 191993, 194451, 195386, 198531, 207694, 212723, 249080, 251403,
    251404, 274438, 279361, 282496, 283262, 283265, 283457, 283460, 283490, 283491, 283494, 283499, 283621, 285930,
    289342, 289507, 289513, 289742, 289743, 289744, 289745, 289763, 289803, 289804, 289818, 290235, 290707, 290785,
    290787, 290912, 290964, 291142, 291252, 291370, 291584, 291600, 291605, 291606, 291714, 292493, 292495, 292496,
    292497, 292500, 292644, 293467, 293765, 293801, 293802, 293814, 293815, 293817, 293818, 293820, 294109, 294680,
    294864, 295040, 295112, 295353, 295354, 295366, 295376, 295377, 295582, 295584, 295667, 295668, 295673, 295743,
    295910, 295913, 296209, 296248, 296753, 296767, 296776, 296777, 296782, 296800, 296873, 297283, 297361, 297810,
    297811, 297814, 297964, 298002, 298066, 298345, 298348, 298822, 298823, 298871, 299050, 299182, 299186, 299324,
    299676, 299687, 299688, 299753, 299862, 300376, 300409, 300430, 300527, 300559, 300572, 300578, 300667, 300668,
    300669, 300670, 300671, 300934, 300936, 300976, 301267, 301285, 301303, 301307, 301392, 301395, 301414, 301415,
    301430, 301437, 301623, 301624, 301625, 302136, 302660, 302820, 302885,
  ]),
  4: new Set([
    2673, 15672, 19602, 20438, 73305, 76778, 109266, 120547, 121810, 130168, 138374, 138403, 149897, 164188, 183981,
    185769, 186295, 186296, 186376, 233794, 233860, 233862, 235125, 238541, 239029, 247909, 250882, 254090, 262019,
    264670, 268869, 268949, 274725, 282459, 282465, 282466, 282516, 282845, 282987, 283010, 283051, 283250, 283252,
    283253, 283255, 283256, 283257, 283258, 283260, 283266, 283296, 283783, 289633, 289760, 289817, 289819, 289820,
    289822, 290056, 290181, 290183, 290184, 290187, 290189, 290237, 290385, 290426, 290742, 290962, 290976, 291368,
    291393, 291540, 292065, 292070, 292075, 292102, 292141, 292147, 292205, 292486, 292492, 292590, 292591, 292606,
    292607, 293075, 293355, 293358, 293360, 293364, 293409, 293413, 293507, 293800, 293803, 294716, 294834, 295521,
    295522, 295528, 295530, 295659, 295661, 295662, 295878, 295880, 295881, 295884, 295885, 296088, 296118, 296128,
    296752, 296773, 296792, 296795, 296797, 296799, 296801, 297059, 297258, 297259, 297260, 297297, 297300, 297490,
    297491, 297996, 298115, 298116, 298131, 298133, 298368, 298704, 298792, 298825, 298826, 298827, 298950, 299197,
    299230, 299340, 299341, 299420, 299752, 300362, 300378, 300410, 300432, 300434, 300557, 300917, 300940, 301305,
    301306, 301371, 301374, 301419, 301424, 301425, 301426, 301427, 301451, 301612, 301613, 301614, 301615, 301617,
    301672, 301674, 301677, 301891, 301895, 302135, 302286, 302611, 302612, 302662, 302913, 302916,
  ]),
  5: new Set([
    2665, 2671, 22329, 43847, 43868, 43877, 95284, 149337, 207052, 221040, 245229, 248761, 282461, 282469, 282471,
    282517, 282518, 282521, 283161, 283163, 283172, 283259, 283264, 283740, 283741, 283781, 283782, 283784, 289764,
    290196, 290198, 290240, 292015, 292032, 292033, 292101, 292103, 292104, 292145, 292217, 292219, 292485, 292488,
    292503, 292593, 292608, 294035, 294108, 294679, 294949, 294950, 295067, 295463, 295467, 295520, 295524, 295813,
    296751, 297287, 297485, 297486, 297962, 298132, 298485, 299049, 299051, 299081, 299332, 299338, 299750, 300365,
    300435, 300558, 300995, 301308, 301367, 301368, 301369, 301373, 301417, 302138, 302171, 302912, 303162, 303480,
  ]),
};

export enum MealPlanCollectionIcons {
  OrderAgain = 'homemade',
  NewAndFresh = 'tasteVerified',
  KidFriendly = 'kidFriendly',
  StaffPick = 'homemade',
  TopRated = 'trophy',
  SpringFavorites = 'spring',
  SummerFavorites = 'summer',
  FallFavorites = 'fall',
  WinterFavorites = 'winter',
  OrganicAndMore = 'organic',
  LimitedTime = 'clock',
  HighProtein = 'highProtein',
  LowCalorie = 'lowCalorie',
}

export const STAFF_PICK_MEAL_PLAN_FOOD_ITEM_IDS: Record<number, Set<number>> = {
  1: new Set([294070]),
  2: new Set([
    109226, 138403, 149897, 153527, 157892, 183981, 196771, 233862, 235125, 251404, 268869, 274725, 282465, 282496,
    282497, 282517, 283010, 283051, 283170, 283172, 283260, 283265, 283457, 283506, 289513, 289742, 289744, 289756,
    289763, 289764, 289820, 289822, 289824, 290056, 290057, 290082, 290083, 290181, 290200, 290237, 290385, 290426,
    290962, 290976, 291605, 292070, 292094, 292096, 292492, 292494, 292496, 293411, 293447, 293507, 293817, 293820,
    294677, 294865, 294871, 295355, 295361, 295379, 295453, 295522, 295530, 295582, 295584, 295668, 295669, 295743,
    295813, 295871, 295878, 295880, 295885, 295908, 295910, 295911, 295913, 296129, 296773, 296800, 296873, 297259,
    297297, 298345, 298950, 299420, 299676, 299714, 299752, 300269, 300376, 300378, 300409, 300557, 301895, 302135,
    302662,
  ]),
  3: new Set([
    2665, 2671, 2673, 2682, 15887, 19602, 22329, 29406, 73305, 73956, 80349, 83249, 107008, 109266, 130168, 149337,
    161020, 170609, 172158, 179702, 185803, 186295, 186296, 186376, 186759, 193403, 194451, 195386, 198531, 212723,
    233583, 233860, 237559, 238541, 239029, 248761, 251402, 254090, 255393, 257513, 274344, 282459, 282516, 282518,
    282987, 283161, 283163, 283174, 283255, 283259, 283262, 283492, 283621, 283740, 287877, 289510, 289633, 289760,
    289937, 290183, 290189, 290233, 290235, 290707, 290742, 290785, 291142, 291252, 291322, 291369, 291370, 291393,
    291601, 291608, 291828, 292012, 292065, 292075, 292141, 292495, 292502, 292591, 293197, 293355, 293362, 293364,
    293409, 293449, 293800, 293803, 293814, 293815, 294233, 294950, 295067, 295112, 295113, 295353, 295365, 295366,
    295372, 295376, 295487, 295528, 295661, 295667, 295673, 295876, 295884, 296127, 296128, 296250, 296795, 297258,
    297283, 297287, 297300, 297361, 297814, 298131, 298132, 298369, 298370, 298485, 298519, 299180, 299198, 299230,
    299332, 299338, 299341, 299555, 299725, 299753, 299862, 300268, 300405, 300410, 300528, 300530, 300559, 300668,
    300934, 300940, 300995, 301128, 301274, 301285, 301302, 301414, 301415, 301426, 301427, 301891, 302136, 302611,
    302612, 302820, 303162,
  ]),
  4: new Set([
    20764, 25517, 25932, 28156, 28510, 29270, 43847, 43877, 63027, 63289, 65804, 76778, 81467, 96639, 109256, 126121,
    126396, 127624, 128051, 138373, 138398, 145427, 148249, 149199, 164188, 169104, 176367, 181628, 182686, 187915,
    188763, 195418, 207052, 207858, 226650, 231102, 233794, 241949, 245229, 247909, 250882, 251403, 262019, 268949,
    272373, 274355, 274547, 276294, 281777, 282461, 282845, 283173, 283253, 283256, 283261, 283264, 283489, 283490,
    283491, 283498, 283499, 283505, 283704, 283741, 283781, 283782, 285930, 286293, 286734, 287129, 289342, 289395,
    289507, 289745, 289818, 289821, 290185, 290193, 290196, 290288, 290394, 290787, 290879, 290964, 291368, 291540,
    291584, 291714, 292032, 292102, 292486, 292497, 292553, 292590, 292607, 292608, 293075, 293358, 293406, 293413,
    293446, 293463, 293465, 293467, 293765, 293818, 294679, 294680, 294716, 294834, 294869, 294873, 294949, 294952,
    295040, 295367, 295436, 295463, 295467, 295521, 295580, 295583, 295867, 295877, 295881, 296084, 296124, 296133,
    296136, 296209, 296248, 296768, 296776, 296790, 296792, 296801, 297059, 297175, 297176, 297260, 297485, 297486,
    297549, 297810, 297812, 297815, 297962, 297964, 297996, 298001, 298002, 298792, 298821, 298822, 298825, 298871,
    299182, 299186, 299197, 299340, 299690, 299691, 299721, 300239, 300365, 300421, 300430, 300434, 300441, 300574,
    300578, 300671, 300936, 301303, 301305, 301306, 301307, 301308, 301318, 301327, 301367, 301373, 301374, 301395,
    301417, 301424, 301425, 301430, 301617, 301622, 301674, 301897, 302125, 302286, 302409, 302415, 302596, 302599,
    302885, 302914, 302915, 303188, 303312, 303323, 303480,
  ]),
  5: new Set([
    2353, 15293, 15612, 15672, 15682, 20438, 21647, 24679, 41389, 43868, 55819, 63615, 65797, 73959, 76785, 77029,
    77932, 81354, 81470, 86180, 95284, 97284, 113023, 115183, 120547, 128513, 131087, 136669, 141523, 143027, 160867,
    160868, 175379, 188767, 190876, 191991, 191993, 193162, 207694, 221040, 249080, 264670, 274438, 275151, 279361,
    282466, 282469, 282471, 282521, 283250, 283252, 283257, 283258, 283266, 283296, 283460, 283461, 283493, 283494,
    283495, 283783, 283784, 285934, 286294, 286701, 286706, 286735, 286742, 289505, 289747, 289803, 289804, 290184,
    290187, 290198, 290240, 290353, 290704, 290788, 290880, 290881, 290909, 290912, 290930, 291582, 291600, 291606,
    292015, 292033, 292101, 292103, 292104, 292105, 292145, 292147, 292205, 292217, 292219, 292485, 292488, 292493,
    292500, 292503, 292593, 292606, 292644, 293360, 293464, 293469, 293799, 293801, 293802, 294035, 294071, 294072,
    294108, 294109, 294216, 294675, 294864, 294868, 295354, 295373, 295375, 295377, 295438, 295520, 295524, 295659,
    295662, 296118, 296125, 296135, 296751, 296752, 296753, 296767, 296777, 296782, 296797, 296799, 297490, 297491,
    297811, 297816, 297819, 297820, 297821, 297963, 298116, 298133, 298348, 298368, 298704, 298823, 298826, 298827,
    298872, 299049, 299050, 299051, 299081, 299324, 299686, 299687, 299688, 299689, 299716, 299750, 300432, 300435,
    300443, 300449, 300558, 300569, 300572, 300575, 300667, 300669, 300670, 300672, 300976, 301267, 301319, 301321,
    301331, 301368, 301369, 301371, 301392, 301419, 301437, 301451, 301612, 301613, 301614, 301615, 301616, 301672,
    301677, 301930, 301938, 302138, 302171, 302594, 302660, 302912, 302913, 302916,
  ]),
};

export const MPHOF_PRE_CUTOFF_BUFFER_HOURS = 48;

export enum LunchCategory {
  HeartyPlates = 'hearty_plates',
  SoupsAndSalads = 'soups_and_salads',
  Handhelds = 'handhelds',
}

export const LUNCH_CATEGORY_DISPLAY_NAMES: Record<LunchCategory, string> = {
  [LunchCategory.HeartyPlates]: 'Hearty Plates',
  [LunchCategory.SoupsAndSalads]: 'Soups & Salads',
  [LunchCategory.Handhelds]: 'Handhelds',
};

export const LUNCH_CATEGORY_DISPLAY_ORDER: Record<LunchCategory, number> = {
  [LunchCategory.HeartyPlates]: 1,
  [LunchCategory.SoupsAndSalads]: 2,
  [LunchCategory.Handhelds]: 3,
};

/**
 * Maps lunch food item IDs to their display category.
 * Add new food item IDs here when creating lunch items via duplicate-food-items-as-lunch.ts.
 * Items not in this map will not appear in the categorized lunch view.
 */
export const LUNCH_FOOD_ITEM_CATEGORY: Record<number, LunchCategory> = {
  // Hearty Plates
  22329: LunchCategory.HeartyPlates,
  23302: LunchCategory.HeartyPlates,
  34269: LunchCategory.HeartyPlates,
  41490: LunchCategory.HeartyPlates,
  41493: LunchCategory.HeartyPlates,
  97284: LunchCategory.HeartyPlates,
  98131: LunchCategory.HeartyPlates,
  98181: LunchCategory.HeartyPlates,
  115534: LunchCategory.HeartyPlates,
  115556: LunchCategory.HeartyPlates,
  131537: LunchCategory.HeartyPlates,
  138373: LunchCategory.HeartyPlates,
  153527: LunchCategory.HeartyPlates,
  160867: LunchCategory.HeartyPlates,
  160868: LunchCategory.HeartyPlates,
  169104: LunchCategory.HeartyPlates,
  176367: LunchCategory.HeartyPlates,
  182325: LunchCategory.HeartyPlates,
  182704: LunchCategory.HeartyPlates,
  195386: LunchCategory.HeartyPlates,
  196093: LunchCategory.HeartyPlates,
  218412: LunchCategory.HeartyPlates,
  225828: LunchCategory.HeartyPlates,
  233860: LunchCategory.HeartyPlates,
  235125: LunchCategory.HeartyPlates,
  246114: LunchCategory.HeartyPlates,
  249127: LunchCategory.HeartyPlates,
  251402: LunchCategory.HeartyPlates,
  256885: LunchCategory.HeartyPlates,
  274344: LunchCategory.HeartyPlates,
  274355: LunchCategory.HeartyPlates,
  275151: LunchCategory.HeartyPlates,
  279361: LunchCategory.HeartyPlates,
  283457: LunchCategory.HeartyPlates,
  283460: LunchCategory.HeartyPlates,
  283461: LunchCategory.HeartyPlates,
  283489: LunchCategory.HeartyPlates,
  283490: LunchCategory.HeartyPlates,
  283740: LunchCategory.HeartyPlates,
  286293: LunchCategory.HeartyPlates,
  290185: LunchCategory.HeartyPlates,
  290288: LunchCategory.HeartyPlates,
  290764: LunchCategory.HeartyPlates,
  291582: LunchCategory.HeartyPlates,
  291600: LunchCategory.HeartyPlates,
  291828: LunchCategory.HeartyPlates,
  292101: LunchCategory.HeartyPlates,
  292102: LunchCategory.HeartyPlates,
  292145: LunchCategory.HeartyPlates,
  292147: LunchCategory.HeartyPlates,
  292507: LunchCategory.HeartyPlates,
  293463: LunchCategory.HeartyPlates,
  293464: LunchCategory.HeartyPlates,
  293814: LunchCategory.HeartyPlates,
  293815: LunchCategory.HeartyPlates,
  294072: LunchCategory.HeartyPlates,
  294108: LunchCategory.HeartyPlates,
  295366: LunchCategory.HeartyPlates,
  295449: LunchCategory.HeartyPlates,
  295463: LunchCategory.HeartyPlates,
  295884: LunchCategory.HeartyPlates,
  295885: LunchCategory.HeartyPlates,
  296118: LunchCategory.HeartyPlates,
  296209: LunchCategory.HeartyPlates,
  296248: LunchCategory.HeartyPlates,
  296678: LunchCategory.HeartyPlates,
  296752: LunchCategory.HeartyPlates,
  297156: LunchCategory.HeartyPlates,
  297809: LunchCategory.HeartyPlates,
  298368: LunchCategory.HeartyPlates,
  298369: LunchCategory.HeartyPlates,
  298370: LunchCategory.HeartyPlates,
  299050: LunchCategory.HeartyPlates,
  299181: LunchCategory.HeartyPlates,
  299339: LunchCategory.HeartyPlates,
  299385: LunchCategory.HeartyPlates,
  299420: LunchCategory.HeartyPlates,
  299716: LunchCategory.HeartyPlates,
  299728: LunchCategory.HeartyPlates,
  299849: LunchCategory.HeartyPlates,
  300363: LunchCategory.HeartyPlates,
  300398: LunchCategory.HeartyPlates,
  300400: LunchCategory.HeartyPlates,
  300405: LunchCategory.HeartyPlates,
  300407: LunchCategory.HeartyPlates,
  300422: LunchCategory.HeartyPlates,
  300430: LunchCategory.HeartyPlates,
  300433: LunchCategory.HeartyPlates,
  300528: LunchCategory.HeartyPlates,
  300544: LunchCategory.HeartyPlates,
  300559: LunchCategory.HeartyPlates,
  300936: LunchCategory.HeartyPlates,
  300985: LunchCategory.HeartyPlates,
  301210: LunchCategory.HeartyPlates,
  301217: LunchCategory.HeartyPlates,
  301218: LunchCategory.HeartyPlates,
  301274: LunchCategory.HeartyPlates,
  301392: LunchCategory.HeartyPlates,
  301466: LunchCategory.HeartyPlates,
  301612: LunchCategory.HeartyPlates,
  301657: LunchCategory.HeartyPlates,
  301658: LunchCategory.HeartyPlates,
  301932: LunchCategory.HeartyPlates,
  301935: LunchCategory.HeartyPlates,
  302169: LunchCategory.HeartyPlates,
  302202: LunchCategory.HeartyPlates,
  302265: LunchCategory.HeartyPlates,
  302617: LunchCategory.HeartyPlates,
  302618: LunchCategory.HeartyPlates,
  302683: LunchCategory.HeartyPlates,
  302931: LunchCategory.HeartyPlates,
  303085: LunchCategory.HeartyPlates,
  303309: LunchCategory.HeartyPlates,
  303355: LunchCategory.HeartyPlates,
  304014: LunchCategory.HeartyPlates,
  304045: LunchCategory.HeartyPlates,
  304257: LunchCategory.HeartyPlates,
  304450: LunchCategory.HeartyPlates,
  304474: LunchCategory.HeartyPlates,
  304486: LunchCategory.HeartyPlates,
  304489: LunchCategory.HeartyPlates,
  304597: LunchCategory.HeartyPlates,
  304625: LunchCategory.HeartyPlates,
  304638: LunchCategory.HeartyPlates,
  304897: LunchCategory.HeartyPlates,
  305089: LunchCategory.HeartyPlates,
  305157: LunchCategory.HeartyPlates,
  305158: LunchCategory.HeartyPlates,
  305230: LunchCategory.HeartyPlates,
  305276: LunchCategory.HeartyPlates,
  305277: LunchCategory.HeartyPlates,
  305375: LunchCategory.HeartyPlates,
  305510: LunchCategory.HeartyPlates,
  305516: LunchCategory.HeartyPlates,
  305591: LunchCategory.HeartyPlates,
  305608: LunchCategory.HeartyPlates,
  305615: LunchCategory.HeartyPlates,
  305678: LunchCategory.HeartyPlates,
  305715: LunchCategory.HeartyPlates,
  305754: LunchCategory.HeartyPlates,
  305755: LunchCategory.HeartyPlates,
  305969: LunchCategory.HeartyPlates,
  305970: LunchCategory.HeartyPlates,
  305971: LunchCategory.HeartyPlates,
  305973: LunchCategory.HeartyPlates,
  306005: LunchCategory.HeartyPlates,
  306040: LunchCategory.HeartyPlates,
  306164: LunchCategory.HeartyPlates,
  306165: LunchCategory.HeartyPlates,
  306183: LunchCategory.HeartyPlates,
  306255: LunchCategory.HeartyPlates,
  306393: LunchCategory.HeartyPlates,
  306395: LunchCategory.HeartyPlates,
  306505: LunchCategory.HeartyPlates,
  306524: LunchCategory.HeartyPlates,
  306573: LunchCategory.HeartyPlates,
  306574: LunchCategory.HeartyPlates,
  306685: LunchCategory.HeartyPlates,
  306721: LunchCategory.HeartyPlates,
  306723: LunchCategory.HeartyPlates,
  306745: LunchCategory.HeartyPlates,
  306746: LunchCategory.HeartyPlates,
  306764: LunchCategory.HeartyPlates,
  306785: LunchCategory.HeartyPlates,
  307045: LunchCategory.HeartyPlates,
  307064: LunchCategory.HeartyPlates,
  307133: LunchCategory.HeartyPlates,
  307183: LunchCategory.HeartyPlates,
  307268: LunchCategory.HeartyPlates,
  307269: LunchCategory.HeartyPlates,
  307270: LunchCategory.HeartyPlates,
  307273: LunchCategory.HeartyPlates,
  307303: LunchCategory.HeartyPlates,
  307305: LunchCategory.HeartyPlates,
  307340: LunchCategory.HeartyPlates,
  307393: LunchCategory.HeartyPlates,
  307395: LunchCategory.HeartyPlates,
  307407: LunchCategory.HeartyPlates,
  307408: LunchCategory.HeartyPlates,
  307409: LunchCategory.HeartyPlates,
  307410: LunchCategory.HeartyPlates,
  307411: LunchCategory.HeartyPlates,
  307412: LunchCategory.HeartyPlates,
  307530: LunchCategory.HeartyPlates,
  193403: LunchCategory.HeartyPlates,
  283163: LunchCategory.HeartyPlates,
  283172: LunchCategory.HeartyPlates,
  300423: LunchCategory.HeartyPlates,
  300432: LunchCategory.HeartyPlates,
  304475: LunchCategory.HeartyPlates,
  305652: LunchCategory.HeartyPlates,
  306399: LunchCategory.HeartyPlates,
  307197: LunchCategory.HeartyPlates,
  307418: LunchCategory.HeartyPlates,
  307634: LunchCategory.HeartyPlates,
  307661: LunchCategory.HeartyPlates,
  307889: LunchCategory.HeartyPlates,
  307891: LunchCategory.HeartyPlates,
  308071: LunchCategory.HeartyPlates,
  308222: LunchCategory.HeartyPlates,
  308312: LunchCategory.HeartyPlates,
  308357: LunchCategory.HeartyPlates,
  308544: LunchCategory.HeartyPlates,
  309004: LunchCategory.HeartyPlates,
  309005: LunchCategory.HeartyPlates,
  309019: LunchCategory.HeartyPlates,
  309021: LunchCategory.HeartyPlates,
  309022: LunchCategory.HeartyPlates,
  309030: LunchCategory.HeartyPlates,
  309122: LunchCategory.HeartyPlates,
  309142: LunchCategory.HeartyPlates,
  309144: LunchCategory.HeartyPlates,
  309243: LunchCategory.HeartyPlates,
  309368: LunchCategory.HeartyPlates,
  309724: LunchCategory.HeartyPlates,
  309726: LunchCategory.HeartyPlates,
  309728: LunchCategory.HeartyPlates,
  309730: LunchCategory.HeartyPlates,
  309791: LunchCategory.HeartyPlates,
  310909: LunchCategory.HeartyPlates,
  310978: LunchCategory.HeartyPlates,
  311328: LunchCategory.HeartyPlates,
  311439: LunchCategory.HeartyPlates,
  311551: LunchCategory.HeartyPlates,
  311553: LunchCategory.HeartyPlates,
  311566: LunchCategory.HeartyPlates,
  311567: LunchCategory.HeartyPlates,
  311655: LunchCategory.HeartyPlates,
  311656: LunchCategory.HeartyPlates,
  312163: LunchCategory.HeartyPlates,
  312375: LunchCategory.HeartyPlates,
  312431: LunchCategory.HeartyPlates,
  312433: LunchCategory.HeartyPlates,
  312489: LunchCategory.HeartyPlates,
  312502: LunchCategory.HeartyPlates,
  312521: LunchCategory.HeartyPlates,
  312610: LunchCategory.HeartyPlates,
  312733: LunchCategory.HeartyPlates,
  312970: LunchCategory.HeartyPlates,
  312990: LunchCategory.HeartyPlates,
  313058: LunchCategory.HeartyPlates,
  313096: LunchCategory.HeartyPlates,
  313314: LunchCategory.HeartyPlates,
  313491: LunchCategory.HeartyPlates,
  313497: LunchCategory.HeartyPlates,
  313499: LunchCategory.HeartyPlates,
  313546: LunchCategory.HeartyPlates,
  313641: LunchCategory.HeartyPlates,
  313995: LunchCategory.HeartyPlates,
  313997: LunchCategory.HeartyPlates,
  314070: LunchCategory.HeartyPlates,
  319512: LunchCategory.HeartyPlates,
  319513: LunchCategory.HeartyPlates,
  319514: LunchCategory.HeartyPlates,

  // Soups & Salads
  28144: LunchCategory.SoupsAndSalads,
  28156: LunchCategory.SoupsAndSalads,
  29270: LunchCategory.SoupsAndSalads,
  33101: LunchCategory.SoupsAndSalads,
  35276: LunchCategory.SoupsAndSalads,
  35281: LunchCategory.SoupsAndSalads,
  64789: LunchCategory.SoupsAndSalads,
  64877: LunchCategory.SoupsAndSalads,
  73305: LunchCategory.SoupsAndSalads,
  77932: LunchCategory.SoupsAndSalads,
  128052: LunchCategory.SoupsAndSalads,
  154262: LunchCategory.SoupsAndSalads,
  161544: LunchCategory.SoupsAndSalads,
  175205: LunchCategory.SoupsAndSalads,
  183981: LunchCategory.SoupsAndSalads,
  223607: LunchCategory.SoupsAndSalads,
  253903: LunchCategory.SoupsAndSalads,
  274438: LunchCategory.SoupsAndSalads,
  274701: LunchCategory.SoupsAndSalads,
  277494: LunchCategory.SoupsAndSalads,
  282462: LunchCategory.SoupsAndSalads,
  282496: LunchCategory.SoupsAndSalads,
  283255: LunchCategory.SoupsAndSalads,
  284152: LunchCategory.SoupsAndSalads,
  285934: LunchCategory.SoupsAndSalads,
  287129: LunchCategory.SoupsAndSalads,
  289505: LunchCategory.SoupsAndSalads,
  289662: LunchCategory.SoupsAndSalads,
  289760: LunchCategory.SoupsAndSalads,
  290198: LunchCategory.SoupsAndSalads,
  290316: LunchCategory.SoupsAndSalads,
  290344: LunchCategory.SoupsAndSalads,
  290704: LunchCategory.SoupsAndSalads,
  291400: LunchCategory.SoupsAndSalads,
  291540: LunchCategory.SoupsAndSalads,
  291601: LunchCategory.SoupsAndSalads,
  291605: LunchCategory.SoupsAndSalads,
  292163: LunchCategory.SoupsAndSalads,
  292500: LunchCategory.SoupsAndSalads,
  292591: LunchCategory.SoupsAndSalads,
  292637: LunchCategory.SoupsAndSalads,
  293197: LunchCategory.SoupsAndSalads,
  293375: LunchCategory.SoupsAndSalads,
  293409: LunchCategory.SoupsAndSalads,
  293411: LunchCategory.SoupsAndSalads,
  293507: LunchCategory.SoupsAndSalads,
  293817: LunchCategory.SoupsAndSalads,
  294897: LunchCategory.SoupsAndSalads,
  294950: LunchCategory.SoupsAndSalads,
  294951: LunchCategory.SoupsAndSalads,
  295458: LunchCategory.SoupsAndSalads,
  295520: LunchCategory.SoupsAndSalads,
  295521: LunchCategory.SoupsAndSalads,
  295522: LunchCategory.SoupsAndSalads,
  295580: LunchCategory.SoupsAndSalads,
  295582: LunchCategory.SoupsAndSalads,
  295667: LunchCategory.SoupsAndSalads,
  295668: LunchCategory.SoupsAndSalads,
  295669: LunchCategory.SoupsAndSalads,
  297140: LunchCategory.SoupsAndSalads,
  297260: LunchCategory.SoupsAndSalads,
  297488: LunchCategory.SoupsAndSalads,
  297962: LunchCategory.SoupsAndSalads,
  298484: LunchCategory.SoupsAndSalads,
  298792: LunchCategory.SoupsAndSalads,
  298871: LunchCategory.SoupsAndSalads,
  299049: LunchCategory.SoupsAndSalads,
  299714: LunchCategory.SoupsAndSalads,
  300268: LunchCategory.SoupsAndSalads,
  300357: LunchCategory.SoupsAndSalads,
  300573: LunchCategory.SoupsAndSalads,
  301212: LunchCategory.SoupsAndSalads,
  301247: LunchCategory.SoupsAndSalads,
  301331: LunchCategory.SoupsAndSalads,
  301674: LunchCategory.SoupsAndSalads,
  302176: LunchCategory.SoupsAndSalads,
  302267: LunchCategory.SoupsAndSalads,
  302660: LunchCategory.SoupsAndSalads,
  302680: LunchCategory.SoupsAndSalads,
  302804: LunchCategory.SoupsAndSalads,
  302805: LunchCategory.SoupsAndSalads,
  303020: LunchCategory.SoupsAndSalads,
  303055: LunchCategory.SoupsAndSalads,
  303138: LunchCategory.SoupsAndSalads,
  303162: LunchCategory.SoupsAndSalads,
  303219: LunchCategory.SoupsAndSalads,
  303242: LunchCategory.SoupsAndSalads,
  303480: LunchCategory.SoupsAndSalads,
  303550: LunchCategory.SoupsAndSalads,
  303855: LunchCategory.SoupsAndSalads,
  303915: LunchCategory.SoupsAndSalads,
  304387: LunchCategory.SoupsAndSalads,
  304526: LunchCategory.SoupsAndSalads,
  304562: LunchCategory.SoupsAndSalads,
  304742: LunchCategory.SoupsAndSalads,
  304894: LunchCategory.SoupsAndSalads,
  304962: LunchCategory.SoupsAndSalads,
  305030: LunchCategory.SoupsAndSalads,
  305223: LunchCategory.SoupsAndSalads,
  305267: LunchCategory.SoupsAndSalads,
  305409: LunchCategory.SoupsAndSalads,
  305483: LunchCategory.SoupsAndSalads,
  305561: LunchCategory.SoupsAndSalads,
  305613: LunchCategory.SoupsAndSalads,
  305638: LunchCategory.SoupsAndSalads,
  305642: LunchCategory.SoupsAndSalads,
  305831: LunchCategory.SoupsAndSalads,
  306032: LunchCategory.SoupsAndSalads,
  306052: LunchCategory.SoupsAndSalads,
  306070: LunchCategory.SoupsAndSalads,
  306083: LunchCategory.SoupsAndSalads,
  306156: LunchCategory.SoupsAndSalads,
  306181: LunchCategory.SoupsAndSalads,
  306210: LunchCategory.SoupsAndSalads,
  306228: LunchCategory.SoupsAndSalads,
  306230: LunchCategory.SoupsAndSalads,
  306232: LunchCategory.SoupsAndSalads,
  306270: LunchCategory.SoupsAndSalads,
  306279: LunchCategory.SoupsAndSalads,
  306291: LunchCategory.SoupsAndSalads,
  306300: LunchCategory.SoupsAndSalads,
  306394: LunchCategory.SoupsAndSalads,
  306497: LunchCategory.SoupsAndSalads,
  306501: LunchCategory.SoupsAndSalads,
  306513: LunchCategory.SoupsAndSalads,
  306530: LunchCategory.SoupsAndSalads,
  306576: LunchCategory.SoupsAndSalads,
  306619: LunchCategory.SoupsAndSalads,
  306649: LunchCategory.SoupsAndSalads,
  306651: LunchCategory.SoupsAndSalads,
  306652: LunchCategory.SoupsAndSalads,
  306675: LunchCategory.SoupsAndSalads,
  306812: LunchCategory.SoupsAndSalads,
  307095: LunchCategory.SoupsAndSalads,
  307150: LunchCategory.SoupsAndSalads,
  307181: LunchCategory.SoupsAndSalads,
  307277: LunchCategory.SoupsAndSalads,
  307367: LunchCategory.SoupsAndSalads,
  307369: LunchCategory.SoupsAndSalads,
  307660: LunchCategory.SoupsAndSalads,
  55796: LunchCategory.SoupsAndSalads,
  65797: LunchCategory.SoupsAndSalads,
  175151: LunchCategory.SoupsAndSalads,
  218340: LunchCategory.SoupsAndSalads,
  283261: LunchCategory.SoupsAndSalads,
  299862: LunchCategory.SoupsAndSalads,
  301226: LunchCategory.SoupsAndSalads,
  305069: LunchCategory.SoupsAndSalads,
  306378: LunchCategory.SoupsAndSalads,
  307198: LunchCategory.SoupsAndSalads,
  307885: LunchCategory.SoupsAndSalads,
  307961: LunchCategory.SoupsAndSalads,
  308245: LunchCategory.SoupsAndSalads,
  308313: LunchCategory.SoupsAndSalads,
  308451: LunchCategory.SoupsAndSalads,
  308724: LunchCategory.SoupsAndSalads,
  309190: LunchCategory.SoupsAndSalads,
  309312: LunchCategory.SoupsAndSalads,
  309313: LunchCategory.SoupsAndSalads,
  309349: LunchCategory.SoupsAndSalads,
  309438: LunchCategory.SoupsAndSalads,
  309527: LunchCategory.SoupsAndSalads,
  309659: LunchCategory.SoupsAndSalads,
  309666: LunchCategory.SoupsAndSalads,
  309795: LunchCategory.SoupsAndSalads,
  309804: LunchCategory.SoupsAndSalads,
  309919: LunchCategory.SoupsAndSalads,
  309999: LunchCategory.SoupsAndSalads,
  310000: LunchCategory.SoupsAndSalads,
  310038: LunchCategory.SoupsAndSalads,
  310741: LunchCategory.SoupsAndSalads,
  310836: LunchCategory.SoupsAndSalads,
  310933: LunchCategory.SoupsAndSalads,
  311417: LunchCategory.SoupsAndSalads,
  311798: LunchCategory.SoupsAndSalads,
  311886: LunchCategory.SoupsAndSalads,
  311926: LunchCategory.SoupsAndSalads,
  311927: LunchCategory.SoupsAndSalads,
  311928: LunchCategory.SoupsAndSalads,
  311942: LunchCategory.SoupsAndSalads,
  312077: LunchCategory.SoupsAndSalads,
  312105: LunchCategory.SoupsAndSalads,
  312231: LunchCategory.SoupsAndSalads,
  312509: LunchCategory.SoupsAndSalads,
  312531: LunchCategory.SoupsAndSalads,
  312749: LunchCategory.SoupsAndSalads,
  312926: LunchCategory.SoupsAndSalads,
  312945: LunchCategory.SoupsAndSalads,
  313161: LunchCategory.SoupsAndSalads,
  313296: LunchCategory.SoupsAndSalads,
  313307: LunchCategory.SoupsAndSalads,
  313500: LunchCategory.SoupsAndSalads,
  313698: LunchCategory.SoupsAndSalads,
  313721: LunchCategory.SoupsAndSalads,
  313988: LunchCategory.SoupsAndSalads,
  314074: LunchCategory.SoupsAndSalads,
  314075: LunchCategory.SoupsAndSalads,
  314109: LunchCategory.SoupsAndSalads,

  // Handhelds
  29413: LunchCategory.Handhelds,
  29508: LunchCategory.Handhelds,
  51016: LunchCategory.Handhelds,
  154566: LunchCategory.Handhelds,
  156190: LunchCategory.Handhelds,
  191991: LunchCategory.Handhelds,
  191993: LunchCategory.Handhelds,
  255472: LunchCategory.Handhelds,
  281777: LunchCategory.Handhelds,
  286706: LunchCategory.Handhelds,
  289400: LunchCategory.Handhelds,
  289507: LunchCategory.Handhelds,
  290184: LunchCategory.Handhelds,
  290962: LunchCategory.Handhelds,
  290976: LunchCategory.Handhelds,
  292032: LunchCategory.Handhelds,
  292497: LunchCategory.Handhelds,
  293358: LunchCategory.Handhelds,
  294533: LunchCategory.Handhelds,
  296084: LunchCategory.Handhelds,
  298145: LunchCategory.Handhelds,
  298822: LunchCategory.Handhelds,
  299753: LunchCategory.Handhelds,
  300269: LunchCategory.Handhelds,
  300366: LunchCategory.Handhelds,
  300416: LunchCategory.Handhelds,
  300417: LunchCategory.Handhelds,
  300419: LunchCategory.Handhelds,
  300425: LunchCategory.Handhelds,
  300426: LunchCategory.Handhelds,
  300619: LunchCategory.Handhelds,
  300935: LunchCategory.Handhelds,
  300937: LunchCategory.Handhelds,
  301930: LunchCategory.Handhelds,
  303405: LunchCategory.Handhelds,
  303664: LunchCategory.Handhelds,
  304488: LunchCategory.Handhelds,
  305448: LunchCategory.Handhelds,
  306575: LunchCategory.Handhelds,
  138398: LunchCategory.Handhelds,
  295377: LunchCategory.Handhelds,
  295673: LunchCategory.Handhelds,
  299051: LunchCategory.Handhelds,
  306627: LunchCategory.Handhelds,
  306628: LunchCategory.Handhelds,
  307374: LunchCategory.Handhelds,
  311241: LunchCategory.Handhelds,
  311576: LunchCategory.Handhelds,
  311925: LunchCategory.Handhelds,
  312002: LunchCategory.Handhelds,
  312082: LunchCategory.Handhelds,
  312678: LunchCategory.Handhelds,
  313470: LunchCategory.Handhelds,
};

export enum ForYouCollectionIds {
  SpringFavorites = 'spring-favorites',
  SummerFavorites = 'summer-favorites',
  FallFavorites = 'fall-favorites',
  WinterFavorites = 'winter-favorites',
  HighProtein = 'high-protein',
  LowCalorie = 'low-calorie',
  KidFriendly = 'kid-friendly',
  OrganicAndMore = 'organic-and-more',
  LimitedTime = 'limited-time',
  StaffPicks = 'staff-picks',
  OrderAgain = 'order-again',
  NewAndFresh = 'new-and-fresh',
  TopRated = 'top-rated',
}

export const FOR_YOU_COLLECTIONS_ORDER = [
  ForYouCollectionIds.SpringFavorites,
  ForYouCollectionIds.SummerFavorites,
  ForYouCollectionIds.FallFavorites,
  ForYouCollectionIds.WinterFavorites,
  ForYouCollectionIds.HighProtein,
  ForYouCollectionIds.LowCalorie,
  ForYouCollectionIds.KidFriendly,
  ForYouCollectionIds.StaffPicks,
  ForYouCollectionIds.OrganicAndMore,
  ForYouCollectionIds.LimitedTime,
  ForYouCollectionIds.OrderAgain,
  ForYouCollectionIds.NewAndFresh,
  ForYouCollectionIds.TopRated,
];

export const MAX_DISHES_PER_FOR_YOU_COLLECTION = 15;
export const MAX_DISHES_FOR_ORDER_AGAIN_COLLECTION = 25;

/**
 * Live thresholds for the macro-driven "High Protein" / "Low Calorie" badges in
 * `getMerchandisingTags`. The For You carousels use stricter heuristic constants
 * (HEALTHY_CAROUSEL_* / *_HEURISTIC_* below).
 */
export const LOW_CALORIE_MAX_CALORIES = 550;

export const HIGH_PROTEIN_MIN_PROTEIN_TENTHS_OF_GRAMS = 380;
// Minimum ratio of grams of protein to grams of total carbohydrate for the High Protein badge.
// 0.8 = at least 0.8g of protein per gram of carbs, e.g. 38g protein with <=47.5g carbs.
// Easier to reason about than protein-kcal share; filters out dishes that hit the protein floor
// only because they're carb-heavy.
export const HIGH_PROTEIN_MIN_PROTEIN_TO_CARB_RATIO = 0.8;

/** Atwater factors. kcal yielded per gram of each macronutrient. */
export const KCAL_PER_GRAM_PROTEIN = 4;
export const KCAL_PER_GRAM_CARB = 4;
export const KCAL_PER_GRAM_FAT = 9;

/** Tunable knobs for the High Protein / Low Calorie heuristic carousels. Values in grams / whole kcal. */
export const HEALTHY_CAROUSEL_SIZE = 20;
export const HEALTHY_CAROUSEL_MIN_RATING_PCT = 88;
export const HEALTHY_CAROUSEL_MIN_REVIEW_COUNT = 5;
export const HEALTHY_CAROUSEL_MIN_KCAL = 200;
export const HEALTHY_CAROUSEL_MACRO_SANITY_TOLERANCE = 0.25;

export const HIGH_PROTEIN_HEURISTIC_MIN_PROTEIN_GRAMS = 35;
export const HIGH_PROTEIN_DENSITY_BONUS_WEIGHT = 50;

export const LOW_CALORIE_HEURISTIC_MAX_KCAL = 600;
export const LOW_CALORIE_HEURISTIC_MIN_PROTEIN_GRAMS = 20;
export const LOW_CALORIE_DENSITY_BONUS_WEIGHT = 200;

export const FALL_FAVORITES_MEAL_PLAN_FOOD_ITEM_IDS: Record<number, Set<number>> = {
  1: new Set([
    304078, 302660, 146820, 303582, 289763, 123555, 290796, 296125, 303662, 301268, 300104, 301270, 295808, 300755,
    302202, 304015, 286329, 294891, 303162, 296678, 283491, 282517, 282459, 82926, 302694,
  ]),
  2: new Set([
    283495, 303908, 289747, 300401, 303661, 302639, 303772, 42595, 157856, 298872, 295757, 292098, 289809, 115556,
    293367, 2403, 300400, 300397, 2401, 292097, 52817, 107008, 300376, 2675, 296768, 244110, 231694, 295878, 20438,
    301891, 298249, 290704, 244122, 241019, 297361, 303320, 241001, 296129, 301285, 297819, 8085, 303324, 297820,
    297300, 292103, 65804, 304229, 244121, 292094, 296801, 295879, 295068, 292105, 303319, 207694, 175205, 295751,
    63615, 41490, 209652, 290742, 290797, 291400, 181628, 293383, 297284, 296124, 268869, 292647, 291540, 295520,
    138373, 295467, 43868, 304045, 292015, 286008, 47995, 295759, 149897, 296795, 79084, 149203, 292099, 115534, 295874,
    300375, 194452, 292644, 290879, 295913, 240994, 299231, 292496, 253903, 244120, 297282, 292012, 41393, 300801,
    303665, 301267, 300360, 300358, 300356, 300357, 300362, 303408, 302612, 300368, 149337, 128052, 300627, 300628,
    300621, 300622, 300443, 299324, 300365, 303854, 300935, 300546, 297802, 154262, 128051, 303407, 300367, 301223,
    300369, 195152, 301375, 300435, 303219, 301333, 302638, 299484, 299482, 301318, 301332, 304076, 304075, 304072,
    160799, 300430, 302179, 156189, 300569, 301331, 304074, 304280, 304287, 304285, 303020, 304722, 303115, 299752,
    304202, 304210, 304201, 304080, 298827, 161544, 302174, 301238, 304073, 303112, 302235, 303056, 303912, 303915,
    78614, 299753, 302176, 302238, 301466, 302634, 302636, 303026, 160048, 302237, 303027, 302268, 302267, 302234,
    300896, 300556, 303059, 233794, 300669, 300559, 303054, 300982, 300561, 300988, 302236, 297550, 297546, 300825,
    303903, 303927, 299720, 290084, 299676, 293820, 206226, 298148, 293447, 302414, 283505, 302915, 297812, 251404,
    299722, 302207, 301194, 298145, 297259, 302411, 299561, 293446, 295579, 283264, 303242, 297490, 299718, 293467,
    294072, 283256, 301195, 300237, 298130, 294868, 294864, 300759, 283498, 298131, 303773, 283296, 299723, 231105,
    290196, 293463, 304014, 282514, 292235, 290341, 290087, 286735, 298146, 298147, 303767, 303770, 303768, 283257,
    294895, 282496, 297260, 299558, 302918, 283265, 299725, 293469, 300791, 286294, 294865, 303902, 292077, 297548,
    282465, 296680, 298143, 283258, 292141, 290909, 290082, 295813, 302916, 300718, 283261, 299010, 299009, 298149,
    301477, 301414, 301616, 303550, 301476, 299690, 301419, 300526, 301417, 299688, 287129, 301475, 301479, 301210,
    302691, 302137, 293075, 302863, 301612, 299185, 15612, 301659, 301478, 290707, 300708, 289633, 304083, 282518,
    295875, 300668,
  ]),
};

export const WINTER_FAVORITES_MEAL_PLAN_FOOD_ITEM_IDS: Record<number, Set<number>> = {
  1: new Set([
    304018, 300963, 300373, 300777, 299728, 292503, 291600, 299730, 299197, 289744, 286116, 299726, 298348, 285930,
    289817, 292590, 109256, 289819, 221040, 298246, 302614, 80349, 151137, 299754, 303024, 245067, 205962, 301467,
  ]),
  2: new Set([
    302263, 300667, 245229, 302632, 303898, 283783, 248761, 290516, 292160, 290391, 297810, 282850, 292159, 289342,
    289514, 294952, 294949, 271436, 299421, 302690, 302885, 300974, 300955, 292495, 173885, 2677, 124495, 155123,
    296086, 296087, 297963, 292492, 300794, 300959, 290785, 127798, 300758, 295113, 283051, 292119, 290288, 292606,
    300805, 297365, 293507, 292096, 299340, 127624, 253808, 292104, 290930, 289820, 291142, 289764, 19602, 295438,
    138398, 124511, 295375, 295369, 293360, 290787, 263133, 263130, 258865, 254505, 164103, 293365, 289745, 289806,
    180369, 295754, 298116, 2673, 295355, 295667, 42597, 253307, 293362, 154566, 292488, 295453, 241910, 300403, 295035,
    289810, 42595, 157856, 298872, 295757, 292098, 289809, 115556, 293367, 2403, 300400, 300397, 2401, 292097, 52817,
    107008, 300376, 2675, 296768, 244110, 231694, 295878, 20438, 301891, 298249, 290704, 244122, 241019, 297361, 303320,
    241001, 296129, 301285, 297819, 8085, 303324, 297820, 297300, 292103, 65804, 304229, 244121, 292094, 296801, 295879,
    295068, 292105, 303319, 207694, 175205, 295751, 63615, 41490, 209652, 290742, 290797, 291400, 181628, 293383,
    297284, 296124, 268869, 292647, 291540, 295520, 138373, 295467, 43868, 304045, 292015, 286008, 47995, 295759,
    149897, 296795, 79084, 149203, 292099, 115534, 295874, 300375, 194452, 292644, 290879, 295913, 240994, 299231,
    292496, 253903, 244120, 297282, 292012, 41393, 300801, 290707, 300708, 289633, 295875, 300105, 300363, 51016,
    303406, 300450, 175379, 300940, 300543, 300549, 190362, 301274, 195186, 303665, 301267, 300360, 300358, 300356,
    300357, 300362, 303408, 302612, 300368, 149337, 128052, 300627, 300628, 300621, 300622, 300443, 299324, 300365,
    303854, 300935, 300546, 297802, 154262, 128051, 303407, 300367, 301223, 300369, 195152, 302631, 303217, 303113,
    300418, 300416, 27994, 301336, 253788, 301369, 303108, 300422, 300901, 300558, 301239, 303086, 299750, 301371,
    233860, 121701, 302173, 302171, 301370, 301465, 303106, 303218, 142550, 300417, 300670, 164188, 302262, 302269,
    28144, 300653, 175143, 300897, 300904, 301248, 301468, 196299, 299063, 301375, 300435, 303219, 301333, 302638,
    299484, 299482, 301318, 301332, 304076, 304075, 304072, 160799, 300430, 302179, 156189, 300569, 301331, 304074,
    304280, 304287, 304285, 303020, 304722, 303115, 299752, 304202, 304210, 304201, 304080, 298827, 161544, 302174,
    301238, 304073, 303112, 302235, 303056, 303912, 303915, 78614, 299753, 302176, 302238, 301466, 302634, 302636,
    303026, 160048, 302237, 303027, 302268, 302267, 302234, 300896, 300556, 303059, 233794, 300669, 300559, 303054,
    300982, 300561, 300988, 302236, 304083, 300668, 297547, 300824, 297059, 196017, 296671, 61777, 290183, 299555,
    294866, 32151, 289507, 300756, 282521, 196773, 302597, 279361, 60406, 45914, 294726, 296209, 297486, 300995, 294680,
    300725, 300819, 247909, 294869, 213957, 300826, 282471, 282466, 283784, 282461, 298144, 299716, 283493, 290353,
    302662, 290339, 274701, 294890, 290200, 294834, 301971, 292212, 293454, 291828, 292163, 292075, 290056, 302596,
    293465, 291252, 292033, 290390, 299387, 30998, 295743, 302413, 283460, 302913, 251403, 283262, 198531, 248356,
    299014, 299715, 297550, 297546, 300825, 303903, 303927, 299720, 290084, 299676, 293820, 206226, 298148, 293447,
    302414, 283505, 302915, 297812, 251404, 299722, 302207, 301194, 298145, 297259, 302411, 299561, 293446, 295579,
    283264, 303242, 297490, 299718, 293467, 294072, 283256, 301195, 300237, 298130, 294868, 294864, 300759, 283498,
    298131, 303773, 283296, 299723, 231105, 290196, 293463, 304014, 282514, 292235, 290341, 290087, 286735, 298146,
    298147, 303767, 303770, 303768, 283257, 294895, 282496, 297260, 299558, 302918, 283265, 299725, 293469, 300791,
    286294, 294865, 303902, 292077, 297548, 282465, 296680, 298143, 283258, 292141, 290909, 290082, 295813, 302916,
    300718, 283261, 299010, 299009, 298149, 282518, 46076, 41262, 186295, 301484, 301420, 302686, 187917, 301212,
    301418, 299850, 299851, 301415, 301421, 207052, 301304, 302138, 44570, 39859, 302693, 271421, 271419, 301483,
    186376, 301818, 301815, 301820, 301429, 15672, 302681, 299855, 301424, 302682, 40188, 301749, 301477, 301414,
    301616, 303550, 301476, 299690, 301419, 300526, 301417, 299688, 287129, 301475, 301479, 301210, 302691, 302137,
    293075, 302863, 301612, 299185, 15612, 301659, 301478,
  ]),
};

export const TASTE_TESTED_SHEF_RATING_COUNT_LOWER_THRESHOLD = 10;
export const TASTE_TESTED_SHEF_RATING_COUNT_UPPER_THRESHOLD = 30;
export const TASTE_TESTED_SHEF_AVERAGE_RATING_THRESHOLD = 76;

export const NEW_SHEF_MAX_ORDERS_FOR_CHIP = 30;
export const POSITIVE_FOOD_ITEM_RATING_PERCENT_THRESHOLD = 75;

export const SEASONAL_FAVORITES_FOOD_ITEM_IDS: Record<SeasonalFavoriteKey, Record<number, Set<number>>> = {
  [SeasonalFavoriteKey.SpringFavorites]: {},
  [SeasonalFavoriteKey.SummerFavorites]: {},
  [SeasonalFavoriteKey.FallFavorites]: FALL_FAVORITES_MEAL_PLAN_FOOD_ITEM_IDS,
  [SeasonalFavoriteKey.WinterFavorites]: WINTER_FAVORITES_MEAL_PLAN_FOOD_ITEM_IDS,
};

/**
 * Macro nutrition field names and their units.
 * Protein, totalCarbohydrate, totalFat, totalSugars, dietaryFiber are to tenths of grams
 * Calories and sodium are to whole numbers
 */
export const MACRO_FIELD_UNITS = {
  calories: 'kcal',
  protein: 'g',
  totalCarbohydrate: 'g',
  totalFat: 'g',
  totalSugars: 'g',
  dietaryFiber: 'g',
  sodium: 'mg',
};

export type MacroFieldName = keyof typeof MACRO_FIELD_UNITS;

/**
 * Region-specific pricing experiment configurations for meal plans.
 * Maps region ID to pricing details (control and treatment price per serving).
 * Used to determine if users have legacy pricing compared to current experiment pricing.
 */
export const REGION_PRICING_EXPERIMENTS: Record<
  number,
  {
    controlPricePerServing: number;
    treatmentPricePerServing: number;
  }
> = {
  // New York
  2: {
    controlPricePerServing: 1450,
    treatmentPricePerServing: 1550,
  },
  // Chicago
  5: {
    controlPricePerServing: 1350,
    treatmentPricePerServing: 1400,
  },
  // Seattle
  6: {
    controlPricePerServing: 1350,
    treatmentPricePerServing: 1400,
  },
  // Los Angeles
  8: {
    controlPricePerServing: 1450,
    treatmentPricePerServing: 1500,
  },
  // San Francisco Bay Area
  1: {
    controlPricePerServing: 1450,
    treatmentPricePerServing: 1500,
  },
};
